import React from 'react';

import cn from 'classnames';
import { motion } from 'framer-motion';
import DOMPurify from 'isomorphic-dompurify';
import { useInView } from 'react-intersection-observer';

import { data_tech_steps } from './content';
import { Icon1, Icon2, Icon3, Icon4, Icon5, Icon6 } from './framer.icons';

const icons = [<Icon1 />, <Icon2 />, <Icon3 />, <Icon4 />, <Icon5 />, <Icon6 />];

export default function StepsWithMiddleLine({ id }) {
  const middleLine2 =
    'absolute left-[50%] top-[calc(6rem+(7.5rem/2))] sm:top-[calc(3rem+34px)] w-[1px] h-[calc(100%-8.75rem-(7.5rem/2)-1rem)] lg:left-[calc(75px/2)] lg:h-[calc(100%-196px)] lg:top-11 sm:left-5 bg-gray-100';

  return (
    <section id={id} className="gl-wrapper gl-wrapper-y relative bg-black pb-[8.75rem]">
      <div className="flex flex-col gap-[56px] sm:gap-5">
        <p className="gl-heading-3 gl-prefix-line mt-2 text-white">{data_tech_steps.prefix}</p>
        <h2
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(data_tech_steps.title) }}
          className="gl-heading-2 text-white [&>span]:text-demo-sa-green"
        />

        <div
          className="max-w-[50%] font-Proxima text-subtitle font-black text-white lg:max-w-full sm:text-[18px]"
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(data_tech_steps.description) }}
        ></div>
      </div>

      <div className={cn('relative mx-auto flex max-w-[61.875rem] flex-col overflow-visible pt-0 lg:pt-8 sm:pt-0')}>
        <span className={middleLine2} />
        <div
          className={cn(
            'absolute left-[calc(50%-5.25rem)] top-[20px] z-[100] w-[10.125rem] transition-[top] delay-700 lg:!hidden',
          )}
        ></div>
        {data_tech_steps.items.map((item, idx) => {
          const { ref, inView } = useInView({
            triggerOnce: true,
            threshold: 1,
            rootMargin: '0px 0px -150px 0px',
          });
          return (
            <div
              key={item.title}
              className="item-wrapper relative flex min-h-[12.1875rem] w-full justify-start pt-11 even:justify-end lg:min-h-[unset] lg:pt-8 sm:pt-8"
            >
              <motion.div
                id={`circle-${idx + 1}`}
                ref={ref}
                viewport={{ once: true }}
                transition={{ duration: 1, delay: 0 }}
                initial={{ scale: 0 }}
                animate={{ scale: inView ? 1 : 0 }}
                className={cn(
                  'circle absolute left-[calc(50%-3.75rem)] top-[45%] z-10 flex aspect-square w-[7.5rem] items-center justify-center rounded-full border border-demo-sa-green bg-black transition-[background-color] duration-700 min-2xl:top-[55%] lg:left-0 lg:top-8 lg:w-[75px] lg:!scale-100 sm:top-8 sm:flex sm:aspect-auto sm:h-fit sm:w-8 sm:items-start sm:!rounded-none sm:!border-none',
                )}
              >
                <div className="stroaked flex h-8 w-8 items-center justify-center py-2 font-Proxima text-white">
                  {icons[idx]}
                </div>
              </motion.div>
              <div className="item-block-wrapper flex min-h-[120px] min-w-[400px] items-center lg:min-h-[unset] lg:w-full lg:min-w-[unset] lg:items-start">
                <motion.div
                  viewport={{ once: true }}
                  initial={{ borderColor: '#000' }}
                  animate={{ borderColor: inView ? '#646262' : '#000' }}
                  transition={{ duration: 0.1, delay: 1.5 }}
                  className={`item-block relative z-10 flex w-full max-w-[25rem] gap-2.5 overflow-hidden border border-gray-80 bg-black p-2.5 lg:ml-[120px] lg:max-w-full lg:!border-gray-80 sm:static sm:ml-9 ${idx % 2 === 0 ? 'left' : 'right'}`}
                >
                  <motion.span
                    viewport={{ once: true }}
                    initial={{ x: '-80px' }}
                    animate={{ x: inView ? 0 : '-80px' }}
                    transition={{ duration: 1, delay: 0.5 }}
                    className="block-number font-Proxima text-subtitle font-black leading-none text-demo-sa-green lg:hidden lg:!transform-none"
                  >
                    {idx + 1}
                  </motion.span>
                  <div className="flex flex-col gap-2">
                    <motion.h3
                      viewport={{ once: true }}
                      initial={{ y: '-125%' }}
                      animate={{ y: inView ? 0 : '-125%' }}
                      transition={{ duration: 1, delay: 0.5 }}
                      className="font-Proxima text-b-sm font-black uppercase text-demo-sa-green lg:!transform-none"
                    >
                      {item.title}
                    </motion.h3>
                    <motion.p
                      transition={{ duration: 1, delay: 0.5 }}
                      viewport={{ once: true }}
                      initial={{ y: '125%' }}
                      animate={{ y: inView ? 0 : '125%' }}
                      className="font-OpenSans font-medium text-white lg:!transform-none"
                    >
                      {item.description}
                    </motion.p>
                  </div>
                </motion.div>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
